'use client';

import React, { useState, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '@/utils/cropImage';
import { DedicationImage } from '@/utils/svgIcons';
import CustomToast from '../CustomToast';
import toast from 'react-hot-toast';
import { TailSpin } from 'react-loader-spinner';
import { useTranslation } from 'next-i18next';
import useUserPreferences from '@/hooks/useUserPreferences';
import { useQuery } from 'react-query';
import { GetSettings } from '@/services/api/settings';
import { useCurrency } from '../DesktopHeader';

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (err) => reject(err);
  });
};

interface ImageUploaderProps {
  croppedImage: string | null;
  setCroppedImage: (img: string | null) => void;
  imageSelected: string;
  setImageSelected: (value: string) => void;
  isEdit: any;
  uuid?: any;
  product?: any;
  effectiveImageSelected?: any;
  croppedFile: File | null;
  setCroppedFile: (file: File | null) => void;
  imageInEdit?: any;
  hasImage?: any;
}

const NotebookImageUploader: React.FC<ImageUploaderProps> = ({
  croppedImage,
  setCroppedImage,
  imageSelected,
  setImageSelected,
  isEdit,
  uuid,
  product,
  effectiveImageSelected,
  croppedFile,
  setCroppedFile,
  imageInEdit,
  hasImage
}) => {
  const {t} = useTranslation('personalizeModal');
  const {locale} = useRouter();
  const preferences = useUserPreferences();
  const { data: settings, isLoading: isSettingsLoading } = useQuery(
    ['settings', preferences?.countryCode, preferences?.currencyCode],
    () => GetSettings(preferences?.countryCode, preferences?.currencyCode)
  );
  
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [originalFile, setOriginalFile] = useState<File | null>(null);

  // Auto-set imageSelected to 'yes' when hasImage is true
  React.useEffect(() => {
    if (hasImage && imageSelected !== 'yes') {
      setImageSelected('yes');
    }
  }, [hasImage, imageSelected, setImageSelected]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.custom((tEl) => (
        <CustomToast
          t={tEl}
          message={t('pictureSizeError')}
          type="error"
        />
      ));
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      const res = await fetch('/api/validate-image', {
        method: 'POST',
        body: formData
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        setLoading(false);
        toast.custom((tEl) => (
          <CustomToast
            t={tEl}
            message={t('imageQualityLowError')}
            type="error"
          />
        ));
        return;
      }

      setLoading(false);
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result as string);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setLoading(false);
      toast.custom((tEl) => (
        <CustomToast
          t={tEl}
          message={t('imageUploadError')}
          type="error"
        />
      ));
    }

    setOriginalFile(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 
      'image/jpeg': ['.jpg', '.jpeg'], 
      'image/png': ['.png'],
      'image/webp': ['.webp']
    },
    multiple: false
  });

  const onCropComplete = useCallback((_: any, croppedArea: any) => {
    setCroppedAreaPixels(croppedArea);
  }, []);

  const handleCrop = async () => {
    if (originalFile && croppedAreaPixels) {
      const reader = new FileReader();
      reader.onload = async () => {
        const croppedFile = await getCroppedImg(
          reader.result as string,
          croppedAreaPixels,
          originalFile.name,
          originalFile.type
        );
        
        const base64 = await fileToBase64(croppedFile);
        setCroppedImage(base64);
        setCroppedFile(croppedFile);
        setShowCropper(false);
      };
      reader.readAsDataURL(originalFile);
    }
  };

  const resetImage = () => {
    localStorage.removeItem(`dedicationImageUrl-${uuid}`);
    setCroppedImage(null);
    setCroppedFile(null);
    setImageSrc(null);
    setZoom(1);
  };

  const currencySymbol = useCurrency(settings?.image_price?.currency || "ILS");
  const imagePrice = settings?.image_price.amount || 0;

  return (
    <>
      <div className="w-full lg:mt-0 rounded-lg p-4 bg-white">
        <div className="flex flex-col items-start justify-center gap-1 lg:gap-0 mb-4">
          <div className="flex items-center justify-center gap-2">
            <DedicationImage />
            <h2 className={`text-md font-semibold ${locale === 'ar' ? 'text-right' : 'text-left'}`}>
              {t('imageUploaderHeading')}
            </h2>
          </div>
          <h4 className={`text-sm font-light ${locale === 'ar' ? 'text-right mr-8' : 'text-left ml-8'}`}>
            {t('imageUploaderSubHeading')}
          </h4>
        </div>

        <div className="mt-4">
          {!croppedImage ? (
            <div
              {...getRootProps()}
              className={`border-2 border-dashed ${
                isDragActive ? 'border-primary' : 'border-gray-400'
              } rounded-lg p-6 cursor-pointer bg-white flex flex-col items-center justify-center transition-colors`}
              id='dedications-image-uploader-button'
            >
              <input {...getInputProps()} />
              {loading ? (
                <div className="relative z-10 flex flex-col items-center justify-center text-center">
                  <TailSpin
                    height="28"
                    width="28"
                    color="#00214D"
                    ariaLabel="tail-spin-loading"
                    radius="1"
                    visible={true}
                  />
                  <p className="mt-4 text-md font-semibold text-primary">
                    {locale === 'ar' ? 'يرجى الانتظار، فنحن نقوم بالتحقق من الصورة.' : 'Please wait, we are checking the image.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-2 mx-auto flex items-center justify-center">
                    <span className="bg-[#00214D14] p-2 rounded-md">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="28"
                        height="28"
                        viewBox="0 0 28 28"
                        fill="none"
                      >
                        <path
                          d="M4.66699 19.8335V22.1668C4.66699 23.4555 5.71166 24.5002 7.00033 24.5002H21.0003C22.289 24.5002 23.3337 23.4555 23.3337 22.1668V19.8335"
                          stroke="#272727"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8.16699 10.4998L14.0003 4.6665L19.8337 10.4998"
                          stroke="#272727"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M14.0003 4.6665V18.6665"
                          stroke="#272727"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                  <p className="text-[#B0BAC8] my-2 text-sm text-center">
                    {isDragActive ? t('dropImageHere') : t('imageInfoText')}
                  </p>
                  <button
                    type="button"
                    className="bg-[#0CDFBF] text-primary text-base font-medium px-6 py-2 rounded-lg mx-auto hover:opacity-90 transition-opacity"
                  >
                    {t('imageUploaderButtonText')}
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('supportedFormats')}: JPG, PNG, WEBP (Max 5MB)
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 border-2 border-dashed border-gray-400 rounded-lg p-3 bg-white">
              <img
                src={croppedImage}
                alt="child Image"
                className="rounded border-2 border-primary w-[144px] h-[144px] object-cover"
              />
              <button
                onClick={resetImage}
                className="bg-[#0CDFBF] text-primary text-base font-medium px-6 py-2 rounded-lg hover:opacity-90 transition-opacity"
              >
                {t('changeImageText')}
              </button>
            </div>
          )}
        </div>

        {showCropper && imageSrc && (
          <div className="fixed inset-0 bg-black bg-opacity-70 z-[999] flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
              <button
                onClick={() => {
                  setShowCropper(false);
                  resetImage();
                }}
                className={`absolute top-3 ${locale === 'ar' ? 'left-3' : 'right-3'} bg-[#93A3B03D] text-xl p-2 rounded-md z-50 hover:bg-gray-200 transition-colors`}
                aria-label="Close cropper"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.23742 7.00023L13.7439 1.88765C14.0857 1.57026 14.0857 1.05566 13.7439 0.738291C13.4021 0.420895 12.8479 0.420895 12.5061 0.738291L7.00022 5.8514L1.49438 0.738291C1.15257 0.420895 0.598385 0.420895 0.256602 0.738291C-0.0851811 1.05569 -0.0852084 1.57028 0.256602 1.88765L5.76302 7.00023L0.256602 12.1128C-0.0852084 12.4302 -0.0852084 12.9448 0.256602 13.2622C0.598412 13.5796 1.15259 13.5796 1.49438 13.2622L7.00022 8.14905L12.5061 13.2622C12.8479 13.5796 13.4021 13.5796 13.7438 13.2622C14.0857 12.9448 14.0857 12.4302 13.7438 12.1128L8.23742 7.00023Z"
                    fill="black"
                  />
                </svg>
              </button>

              <h3 className={`text-lg font-semibold mb-4 ${locale === 'ar' ? 'text-right' : 'text-left'}`}>
                {t('cropYourImage')}
              </h3>

              <div className="relative w-full h-64 bg-gray-100 rounded-md overflow-hidden">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropSize={{ width: 250, height: 250 }}
                  minZoom={1}
                  maxZoom={3}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>

              <div className="mt-4 flex flex-col items-center">
                <div className="flex items-center justify-center gap-2 w-full mb-2">
                  <span className="text-sm">{t('zoom')}:</span>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="w-full"
                  />
                  <span className="text-sm w-12">{zoom.toFixed(1)}x</span>
                </div>

                <p className="text-sm text-gray-600 mb-4 text-center">
                  {t('adjustImagePosition')}
                </p>

                <div className="flex gap-3 w-full">
                  <button
                    onClick={() => {
                      setShowCropper(false);
                      resetImage();
                    }}
                    className="flex-1 border border-gray-300 text-gray-700 text-base font-medium px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleCrop}
                    className="flex-1 bg-primary text-white text-base font-medium px-4 py-3 rounded-lg hover:opacity-90 transition-opacity"
                  >
                    {t('confirmImageAdditionText')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NotebookImageUploader;